import { startAuthentication, startRegistration, browserSupportsWebAuthn } from "@simplewebauthn/browser";
import { api } from "./api";

// Face ID / Touch ID (platform passkey) support on this device.
export async function faceIdSupported() {
    try {
        if (!browserSupportsWebAuthn() || !window.PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable) return false;
        return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    } catch { return false; }
}

export const isAppleDevice = () => /iPhone|iPad|iPod|Macintosh/.test(navigator.userAgent) && "ontouchend" in document;
export const biometricName = () => (/iPhone|iPad|iPod/.test(navigator.userAgent) ? "Face ID" : /Macintosh/.test(navigator.userAgent) ? "Touch ID" : "Biometric");

const deviceLabel = () => {
    const ua = navigator.userAgent;
    const standalone = window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone;
    const dev = /iPhone/.test(ua) ? "iPhone" : /iPad/.test(ua) ? "iPad" : /Macintosh/.test(ua) ? "Mac" : /Android/.test(ua) ? "Android" : "This device";
    return `${dev}${standalone ? " (Home Screen app)" : ""}`;
};

// Remembers that THIS device has a passkey for THIS site, so the sign-in button only
// shows where Face ID will actually work (otherwise iOS falls back to QR / security key).
const FLAG = () => `rosterly_faceid_${window.location.hostname}`;
export const hasLocalFaceId = () => { try { return !!localStorage.getItem(FLAG()); } catch { return false; } };
const markLocal = () => { try { localStorage.setItem(FLAG(), "1"); } catch { /* noop */ } };
export const clearLocalFaceId = () => { try { localStorage.removeItem(FLAG()); } catch { /* noop */ } };

export async function registerFaceId() {
    const { data: optionsJSON } = await api.post("/passkeys/registration/options");
    const credential = await startRegistration({ optionsJSON: { ...optionsJSON, hints: ["client-device"] } });
    await api.post("/passkeys/registration/verify", { credential, label: deviceLabel() });
    markLocal();
}

// Returns { token, user } — same shape as Google / email sign-in.
export async function signInWithFaceId() {
    const { data } = await api.post("/passkeys/authentication/options");
    const credential = await startAuthentication({ optionsJSON: { ...data.options, hints: ["client-device"] } });
    const { data: res } = await api.post("/passkeys/authentication/verify", { credential, state: data.state });
    markLocal();
    return res;
}

export const passkeyError = (e) => {
    if (e?.name === "NotAllowedError") return "Cancelled — or Face ID isn't set up for this site on this device yet.";
    if (e?.response?.status === 401) { clearLocalFaceId(); }
    if (e?.name === "InvalidStateError") return "This device already has Face ID sign-in set up.";
    return e?.response?.data?.detail || e?.message || "Something went wrong.";
};
